import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/api/client';
import { useDebounced } from '@/components/parameters/useDebounced';
import { Pager } from '@/components/Pager';
import { StatusPill } from '@/components/StatusPill';
import { HiddenReportsCard } from '@/components/HiddenReportsCard';
import { ReassignOwnerDialog } from '@/components/ReassignOwnerDialog';
import { subscriptionTitle } from '@/types';
import type { BulkReassignOwnerResult, DirectoryUser, SubscriptionSummary } from '@/types';

/** Matches My Subscriptions and the KT Insights history, so every list here pages alike. */
const pageSize = 50;

/** What the reassign dialog is currently open for: one row, or the whole current selection. */
type ReassignTarget =
  | { kind: 'single'; subscription: SubscriptionSummary }
  | { kind: 'bulk'; ids: string[] }
  | null;

/** The banner shown once a reassignment completes — deliberately not a generic "Success". */
type ReassignOutcome = { count: number; failed: number; newOwnerName: string } | null;

export function Admin() {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const failures = useQuery({ queryKey: ['admin-failures'], queryFn: () => api.listFailures(1440) });
  const stale = useQuery({ queryKey: ['admin-stale'], queryFn: () => api.listStale(7) });
  const unowned = useQuery({ queryKey: ['admin-unowned'], queryFn: api.listUnowned });

  const [ownerFilter, setOwnerFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reassignTarget, setReassignTarget] = useState<ReassignTarget>(null);
  const [outcome, setOutcome] = useState<ReassignOutcome>(null);

  // Typing in the owner box now costs a request, so it waits for the typing to stop. 250ms is the
  // same delay the search box on My Subscriptions uses.
  const settledOwnerFilter = useDebounced(ownerFilter.trim(), 250);

  // A new filter invalidates the offset — page 3 of the old result set is meaningless against the
  // new one and is usually past its end, which would show an empty table rather than the matches.
  useEffect(() => setSkip(0), [settledOwnerFilter]);

  /**
   * One page of the estate, plus the two roll-ups this screen is built out of.
   *
   * <b>The owner filter and the by-owner grouping both had to move to the server with the paging.</b>
   * They were a `filter` and a `reduce` over the full array, and neither survives being handed 50
   * rows: the filter would match only within the current page, and the grouping — the thing an
   * admin uses to FIND the owner whose mail is broken — would have shown "3 failing" for someone
   * with 30. The API returns them computed over the whole visible list, so the buttons still name
   * every owner in the estate and still count all of their subscriptions.
   */
  const all = useQuery({
    queryKey: ['admin-all', settledOwnerFilter, skip],
    queryFn: () => api.listAllSubs({ owner: settledOwnerFilter || undefined, skip, take: pageSize }),
    // Without this the table empties between pages and the layout jumps.
    placeholderData: keepPreviousData
  });

  const rows = all.data?.subscriptions ?? [];
  const total = all.data?.total ?? 0;
  const ownerGroups = all.data?.ownerGroups ?? [];

  const single = useMutation({
    mutationFn: (user: DirectoryUser) => {
      if (reassignTarget?.kind !== 'single') return Promise.reject(new Error('No subscription selected.'));
      return api.reassignOwner(reassignTarget.subscription.subscriptionId, user.objectId, user.displayName);
    },
    onSuccess: (_result, user) => {
      setOutcome({ count: 1, failed: 0, newOwnerName: user.displayName });
      setReassignTarget(null);
      void qc.invalidateQueries({ queryKey: ['admin-all'] });
      void qc.invalidateQueries({ queryKey: ['admin-unowned'] });
    }
  });

  const bulk = useMutation({
    mutationFn: (user: DirectoryUser) => {
      if (reassignTarget?.kind !== 'bulk') return Promise.reject(new Error('No subscriptions selected.'));
      return api.bulkReassignOwner(reassignTarget.ids, user.objectId, user.displayName);
    },
    onSuccess: (results: BulkReassignOwnerResult[], user) => {
      const failed = results.filter(r => !r.success).length;
      setOutcome({ count: results.length - failed, failed, newOwnerName: user.displayName });
      setReassignTarget(null);
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['admin-all'] });
      void qc.invalidateQueries({ queryKey: ['admin-unowned'] });
    }
  });

  const busy = single.isPending || bulk.isPending;
  const dialogError = single.isError ? describeError(single.error) : bulk.isError ? describeError(bulk.error) : null;

  function toggleSelected(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const pageAllSelected = rows.length > 0 && rows.every(s => selected.has(s.subscriptionId));

  /**
   * Ticks or unticks this page's rows, leaving the rest of the selection alone.
   *
   * It used to select every filtered row, which was every row there was. With a page it can only
   * honestly offer the page — but the selection itself deliberately survives paging, so working
   * through a long list a page at a time and then reassigning the lot in one go still works. Only
   * ids are kept because that is all the bulk endpoint takes.
   */
  function toggleSelectAllOnPage() {
    setSelected(prev => {
      const next = new Set(prev);
      for (const s of rows) {
        if (pageAllSelected) next.delete(s.subscriptionId);
        else next.add(s.subscriptionId);
      }
      return next;
    });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Admin overview</h1>
          <div className="sub">All subscriptions across the tenant</div>
        </div>
      </div>

      <div className="stat-grid">
        {/* `totalUnfiltered`, not the page and not the filtered total — this tile is the size of the
            estate, and it must not move when somebody types in the owner box. */}
        <Stat label="Total subscriptions" value={all.data?.totalUnfiltered ?? '—'} />
        <Stat label="Failures (24h)" value={failures.data?.length ?? '—'} variant="alert" />
        <Stat label="Stale (7d+)" value={stale.data?.length ?? '—'} variant="warn" />
        <Stat label="Unclaimed in the portal" value={unowned.data?.length ?? '—'} />
      </div>

      {outcome && (
        <div className="notice notice-warn" style={{ marginBottom: 18 }}>
          <strong>
            Updated the portal owner for {outcome.count} subscription{outcome.count === 1 ? '' : 's'} to{' '}
            {outcome.newOwnerName}.
            {outcome.failed > 0 &&
              ` ${outcome.failed} could not be reassigned — reload and try those again.`}
          </strong>
          <div>
            This changed the portal&rsquo;s own ownership record only. It does <strong>not</strong> change who
            the report server sends mail as — check the &ldquo;Sends as (Report Server)&rdquo; column below. If it
            still names a broken account, that subscription&rsquo;s mail delivery will keep failing until the
            report server record is fixed there directly.
          </div>
          <button className="btn-secondary" style={{ marginTop: 10 }} onClick={() => setOutcome(null)}>
            Dismiss
          </button>
        </div>
      )}

      <HiddenReportsCard />

      {ownerGroups.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <h3>Subscriptions by report-server owner</h3>
          </div>
          <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {/* The roll-up covers the whole estate but the strip only shows the first 24 — it is
                ordered failing-first, so what is cut is the tail nobody is looking for. */}
            {ownerGroups.slice(0, 24).map(g => (
              <button
                key={g.name ?? '(none)'}
                className="btn-secondary"
                style={g.failing > 0 ? { borderColor: 'var(--kt-red)', color: 'var(--kt-red)' } : undefined}
                // A null name means the report server holds no owner for those rows. There is
                // nothing to type into the box, so pressing it clears the filter rather than
                // filtering on a placeholder that matches nothing.
                onClick={() => setOwnerFilter(g.name ?? '')}
                title={g.name
                  ? 'Filter the table below to this owner'
                  : 'These have no owner on the report server, so there is no name to filter by'}>
                {g.name ?? '(no report-server owner on file)'} — {g.total}
                {g.failing > 0 && <> ({g.failing} failing)</>}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div
          className="card-header"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3>All subscriptions</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className="input"
              style={{ width: 280 }}
              placeholder="Filter by owner (portal or report server)…"
              value={ownerFilter}
              onChange={e => setOwnerFilter(e.target.value)}
            />
            {selected.size > 0 && (
              <button className="btn-primary" onClick={() => setReassignTarget({ kind: 'bulk', ids: [...selected] })}>
                Reassign owner ({selected.size})
              </button>
            )}
          </div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={pageAllSelected}
                  onChange={toggleSelectAllOnPage}
                  aria-label="Select every subscription on this page"
                  title="Selects every subscription on this page. Rows ticked on other pages stay ticked."
                />
              </th>
              <th>Report</th>
              <th title="Who the portal's own record says owns this — used for &quot;my subscriptions&quot; and edit permission.">
                Portal owner
              </th>
              <th title="The report server's own record: the Windows account it actually sends mail as.">
                Sends as (Report Server)
              </th>
              <th>Schedule</th>
              <th>Last run</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map(s => (
              <tr key={s.subscriptionId} onClick={() => navigate(`/subscriptions/${s.subscriptionId}`)}>
                <td onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(s.subscriptionId)}
                    onChange={() => toggleSelected(s.subscriptionId)}
                    aria-label={`Select ${subscriptionTitle(s)}`}
                  />
                </td>
                <td>
                  {/* Its own name where it has one, with the path underneath either way — the same
                      pairing as My Subscriptions, so a named subscription still says which report
                      it runs. */}
                  <div className="report-name">{subscriptionTitle(s)}</div>
                  <div className="report-path">{s.reportPath}</div>
                </td>
                <td>
                  {s.portalOwnerDisplayName ?? (
                    <span style={{ color: 'var(--kt-grey-400)' }}>Not claimed in the portal</span>
                  )}
                </td>
                <td>
                  {s.ownerDisplayName ?? <span style={{ color: 'var(--kt-orange)', fontWeight: 600 }}>Unowned</span>}
                </td>
                <td>{s.scheduleDescription}</td>
                <td>{s.lastRunUtc ? new Date(s.lastRunUtc).toLocaleString() : '—'}</td>
                <td><StatusPill status={s.lastStatus} /></td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="btn-secondary" onClick={() => setReassignTarget({ kind: 'single', subscription: s })}>
                    Reassign
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: 'var(--kt-grey-500)' }}>
                  {ownerFilter
                    ? <>No subscriptions match &ldquo;{ownerFilter}&rdquo;.</>
                    : 'No subscriptions.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pager
        skip={skip}
        take={pageSize}
        shown={rows.length}
        total={total}
        busy={all.isFetching}
        onChange={setSkip}
      />

      {reassignTarget && (
        <ReassignOwnerDialog
          title="Reassign subscription owner"
          subject={
            reassignTarget.kind === 'single' ? (
              <strong>{subscriptionTitle(reassignTarget.subscription)}</strong>
            ) : (
              <strong>
                {reassignTarget.ids.length} selected subscription{reassignTarget.ids.length === 1 ? '' : 's'}
              </strong>
            )
          }
          busy={busy}
          error={dialogError}
          onCancel={() => { setReassignTarget(null); single.reset(); bulk.reset(); }}
          onConfirm={user => (reassignTarget.kind === 'single' ? single.mutate(user) : bulk.mutate(user))}
        />
      )}
    </>
  );
}

function Stat({ label, value, variant }: { label: string; value: number | string; variant?: 'alert' | 'warn' }) {
  return (
    <div className={`stat ${variant ?? ''}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message.replace(/^API \d+: /, '') : 'Something went wrong.';
}
